import { useEffect, useState } from 'react';
import { loginStart, loginAuth, UnauthorizedError } from '../api';
import { getQueryParamFromLocation, validateIsEmail } from '../utils';
import { useGlobalContext } from '../state/GlobalContext';
import { usePageVisibility } from '../../utils/usePageVisibility';

export enum Steps {
  start,
  auth,
}

export enum SentCodePaths {
  email,
  emailAndPhone,
}

export const useLogin = ({ shopDomain }: { shopDomain: string }) => {
  const {
    setAuthToken: setAuthTokenContext,
    setCustomerId: setCustomerIdContext,
    loadLocalStorageSession,
  } = useGlobalContext();
  const pageVisibility = usePageVisibility();

  const [step, setStep] = useState<Steps>(Steps.start);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sentCodePath, setSentCodePath] = useState<SentCodePaths | null>(null);

  const [customerId, setCustomerId] = useState<string | null>(
    (getQueryParamFromLocation('customerId') as string) || null,
  );
  const [email, setEmail] = useState<string>(
    validateIsEmail(getQueryParamFromLocation('email') as string)
      ? (getQueryParamFromLocation('email') as string)
      : '',
  );
  const [code, setCode] = useState<string>(
    (getQueryParamFromLocation('code') as string) || '',
  );

  useEffect(() => {
    // if code an customerId in query params we go to auth step
    if (code && customerId) {
      setStep(Steps.auth);
      auth();
    }
  }, []);

  // on tab focus we check if we already have started session in another tab
  // for example opening a link in the email
  useEffect(() => {
    loadLocalStorageSession();
  }, [pageVisibility]);

  const start = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { success, data } = await loginStart({ email, shopDomain });

      if (success && data.status === 'no_subscriptions_customer') {
        if (data.shopifyCustomer) {
          // redirect to shopify login

          const shopifyRoot =
            (window.Shopify?.routes?.root || '/').replace(/(.*)(\/)$/, '$1') ||
            '';

          const url = new URL(
            `${shopifyRoot}account/login`,
            window.location.origin,
          );

          url.searchParams.append('keepShopifyLogin', '');
          url.searchParams.append('email', email);

          window.location.href = url.href;
        } else {
          setError('login.invalid_email_no_order');
        }
      } else if (success && data.status) {
        setSentCodePath(
          data.status === 'code_sent_email'
            ? SentCodePaths.email
            : SentCodePaths.emailAndPhone,
        );
        setCustomerId(data.customerId);
        setStep(Steps.auth);
      } else {
        setError('login.network_error_abuse');
      }
    } catch (err) {
      console.error(err);
      setError('login.network_error');
    } finally {
      setIsLoading(false);
    }
  };

  const auth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { success, data } = await loginAuth({
        customerId: customerId as string,
        code: code as string,
        shopDomain,
      });

      if (success && data.authToken) {
        setAuthTokenContext(data.authToken);
        setCustomerIdContext(customerId);
      } else {
        setIsLoading(false);
        setError('login.invalid_code');
      }
    } catch (err: any) {
      if (err instanceof UnauthorizedError) {
        setError('login.invalid_code');
      } else {
        setError('login.network_error');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resetLogin = () => {
    setStep(Steps.start);
    setCode('');
    setError(null);
  };

  return {
    step,
    isLoading,
    error,
    email,
    code,
    sentCodePath,
    setEmail,
    setCode,
    setError,
    start,
    auth,
    resetLogin,
  };
};
